import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    TextField,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Divider,
    Tooltip,
    MenuItem
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { db } from "../../firebase";
import {
    collection,
    onSnapshot,
    deleteDoc,
    doc,
    updateDoc,
    query,
    orderBy,
    where,
    Timestamp 
} from "firebase/firestore";

function Viewtrips() {
    const navigate = useNavigate();
    const [trips, setTrips] = useState([]);
    const [search, setSearch] = useState("");
    const [productList, setProductList] = useState([]); // ✅ For Product Select Box

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(200);

    const [editOpen, setEditOpen] = useState(false);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [editData, setEditData] = useState({});

    const [imageOpen, setImageOpen] = useState(false);
    const [selectedImages, setSelectedImages] = useState({ tarePhotos: [], grossPhotos: [] });
    const [selectedDate, setSelectedDate] = useState(
        new Date().toISOString().split("T")[0] // default = today
    );

    const handleViewImages = (trip) => {
        setSelectedImages({
            tarePhotos: trip.tarePhotos || [],
            grossPhotos: trip.grossPhotos || []
        });
        setImageOpen(true);
    };

    // ✅ FETCH TRIPS
    useEffect(() => {
        const start = new Date(selectedDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(selectedDate);
        end.setHours(23, 59, 59, 999);

        const q = query(
            collection(db, "trips"),
            where("status", "==", "Completed"),
            where("grossDate", ">=", start),
            where("grossDate", "<=", end),
            orderBy("grossDate", "desc")
        );

        const unsub = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter(trip =>
                    !trip.products?.some(p =>
                        (p.productName || "").toLowerCase().includes("bolder")
                    )
                );

            setTrips(data);
        });

        return () => unsub();
    }, [selectedDate]);

    // ✅ FETCH PRODUCTS FOR THE SELECT BOX
    useEffect(() => {
        const q = collection(db, "trip_products");
        const unsubProducts = onSnapshot(q, (snapshot) => {
            const products = snapshot.docs.map(doc => doc.data().productName).filter(Boolean);
            setProductList(Array.from(new Set(products))); // Get unique product names
        });
        return () => unsubProducts();
    }, []);

    // DELETE TRIP
    const handleDelete = async (id) => {
        Swal.fire({
            title: "Delete Trip?",
            text: "This record will be permanently deleted.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Yes Delete"
        }).then(async (result) => {
            if (result.isConfirmed) {
                await deleteDoc(doc(db, "trips", id));
                Swal.fire("Deleted!", "Trip removed successfully", "success");
            }
        });
    };

    // FORMAT DATE FOR INPUT
    const formatForInput = (timestamp) => {
        if (!timestamp) return "";
        const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const tzoffset = (new Date()).getTimezoneOffset() * 60000; 
        return (new Date(d - tzoffset)).toISOString().slice(0, 16);
    };

    // OPEN QUICK EDIT DIALOG
    const handleQuickEdit = (trip) => {
        setSelectedTrip(trip.id);
        setEditData({
            ...trip,
            grossDateInput: formatForInput(trip.grossDate),
            tareDateInput: formatForInput(trip.tareDate),
            products: trip.products ? JSON.parse(JSON.stringify(trip.products)) : [] 
        });
        setEditOpen(true);
    };

    // ✅ ADD NEW BLANK PRODUCT ROW
    const handleAddProduct = () => {
        const updatedProducts = [...(editData.products || []), { productName: "", qty: 0, rate: 0, amount: 0 }];
        setEditData({ ...editData, products: updatedProducts });
    };

    // ✅ DELETE A PRODUCT ROW
    const handleDeleteProduct = (index) => {
        const updatedProducts = editData.products.filter((_, i) => i !== index);

        // Recalculate Totals
        const newTotalAmount = updatedProducts.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
        const paid = Number(editData.paidAmount) || 0;
        const newRemaining = Math.max(0, newTotalAmount - paid);

        setEditData({
            ...editData,
            products: updatedProducts,
            totalAmount: newTotalAmount,
            remainingAmount: newRemaining
        });
    };

    // ✅ EDIT PRODUCT DETAILS
    const handleEditProductChange = (index, field, value) => {
        const updatedProducts = [...editData.products];
        updatedProducts[index][field] = field === 'productName' ? value : Number(value);

        // Auto calculate Amount if Qty or Rate changes
        if (field === 'qty' || field === 'rate') {
            updatedProducts[index].amount = Math.round(
                (Number(updatedProducts[index].qty) || 0) * (Number(updatedProducts[index].rate) || 0)
            );
        }

        // Recalculate Grand Total & Remaining amount
        const newTotalAmount = updatedProducts.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
        const paid = Number(editData.paidAmount) || 0;
        const newRemaining = Math.max(0, newTotalAmount - paid);

        setEditData({
            ...editData,
            products: updatedProducts,
            totalAmount: newTotalAmount,
            remainingAmount: newRemaining
        });
    };

    // SAVE UPDATES TO FIRESTORE
    const handleUpdate = async () => {
        try {
            const newGrossDate = editData.grossDateInput ? new Date(editData.grossDateInput) : null;
            const newTareDate = editData.tareDateInput ? new Date(editData.tareDateInput) : null;

            const updatePayload = {
                customerName: editData.customerName || editData.partyName || "",
                vehicleNo: editData.vehicleNo || "",
                mobile: editData.mobile || "",
                location: editData.location || "",
                products: editData.products || [],
                totalAmount: editData.totalAmount || 0,
                remainingAmount: editData.remainingAmount || 0
            };

            if (newGrossDate) updatePayload.grossDate = Timestamp.fromDate(newGrossDate);
            if (newTareDate) updatePayload.tareDate = Timestamp.fromDate(newTareDate);

            await updateDoc(doc(db, "trips", selectedTrip), updatePayload);

            Swal.fire("Updated", "Trip modified successfully", "success");
            setEditOpen(false);
        } catch (err) {
            Swal.fire("Error", err.message, "error");
        }
    };

    const filteredTrips = trips.filter((trip) =>
        JSON.stringify(trip).toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Box sx={{
            bgcolor: '#f4f6f8',
            minHeight: '100vh',
            p: 1,
            "& .MuiInputBase-root": { fontSize: "14px" },
            "& .MuiTableCell-root": { fontSize: "13px", padding: "2px 2px" },
            "& .MuiButton-root": { fontSize: "11px", padding: "4px 8px" }
        }}>
            <Paper sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Trips List</Typography>

                    <TextField
                        type="date"
                        size="small"
                        label="Filter Date"
                        InputLabelProps={{ shrink: true }}
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                    />

                    <TextField
                        size="small"
                        label="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Box>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ background: "#f5f5f5" }}>
                                <TableCell>SR</TableCell>
                                <TableCell>Ticket</TableCell>
                                <TableCell>Party</TableCell>
                                <TableCell>Vehicle</TableCell>
                                <TableCell>Product</TableCell>
                                <TableCell align="center">Qty</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell>Paid</TableCell>
                                <TableCell>Remain</TableCell>
                                <TableCell>Pay Mode</TableCell>
                                <TableCell>Net Wt</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTrips
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => {
                                    const totalSale =
                                        row.products?.reduce((sum, p) => sum + (Number(p.amount) || 0), 0) || 0;

                                    return (
                                        <TableRow key={row.id} hover>
                                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                            <TableCell>{row.ticketNo || "-"}</TableCell>
                                            <TableCell>{row.customerName}</TableCell>
                                            <TableCell>{row.vehicleNo}</TableCell>
                                            <TableCell>
                                                {row.products?.map((p, i) => (
                                                    <Typography key={i} sx={{ fontSize: "0.8rem" }}>
                                                        {i + 1}) {p.productName}
                                                    </Typography>
                                                ))}
                                            </TableCell>
                                            <TableCell align="center">
                                                {row.products?.map((p, i) => (
                                                    <Typography key={i} sx={{ fontSize: "0.8rem" }}>
                                                        {p.qty}
                                                    </Typography>
                                                ))}
                                            </TableCell>
                                            <TableCell>
                                                <Typography fontWeight="bold">
                                                    ₹ {row.totalAmount || totalSale}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography color="green" fontWeight="bold">
                                                    ₹ {row.paidAmount || 0}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography color={(row.remainingAmount || 0) > 0 ? "error" : "success"} fontWeight="bold">
                                                    ₹ {row.remainingAmount || 0}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {row.payments && row.payments.length > 0 ? (
                                                    row.payments.map((p, i) => (
                                                        <Typography key={i} sx={{ fontSize: "0.75rem" }}>
                                                            {p.mode} - ₹{p.amount}
                                                        </Typography>
                                                    ))
                                                ) : "-"}
                                            </TableCell>
                                            <TableCell>{row.netWeight}</TableCell>
                                            <TableCell>
                                                {row.grossDate ?
                                                    new Intl.DateTimeFormat('en-GB', {
                                                        day: '2-digit', month: '2-digit', year: 'numeric',
                                                        hour: '2-digit', minute: '2-digit', second: '2-digit'
                                                    }).format(row.grossDate.toDate())
                                                    : ""}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Full Edit">
                                                    <IconButton color="primary" onClick={() => navigate(`/edit-trip/${row.id}`)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Quick Edit Dates & Products">
                                                    <IconButton color="warning" onClick={() => handleQuickEdit(row)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="View Photos">
                                                    <IconButton color="secondary" onClick={() => handleViewImages(row)}>
                                                        📷
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Delete Trip">
                                                    <IconButton color="error" onClick={() => handleDelete(row.id)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={filteredTrips.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* ✅ QUICK EDIT MODAL (Using Bootstrap-style classes) */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Quick Edit Trip</DialogTitle>
                <DialogContent dividers>
                    <Grid className="row" spacing={2} sx={{ mx: 0 }}>
                        {/* Basic Details */}
                        <Grid className="col-sm-6 mb-3">
                            <TextField label="Customer Name" fullWidth size="small" value={editData.customerName || ""} onChange={(e) => setEditData({ ...editData, customerName: e.target.value })} />
                        </Grid>
                        <Grid className="col-sm-6 mb-3">
                            <TextField label="Vehicle No" fullWidth size="small" value={editData.vehicleNo || ""} onChange={(e) => setEditData({ ...editData, vehicleNo: e.target.value })} />
                        </Grid>
                        <Grid className="col-sm-6 mb-3">
                            <TextField label="Mobile" fullWidth size="small" value={editData.mobile || ""} onChange={(e) => setEditData({ ...editData, mobile: e.target.value })} />
                        </Grid>
                        <Grid className="col-sm-6 mb-3">
                            <TextField label="Location" fullWidth size="small" value={editData.location || ""} onChange={(e) => setEditData({ ...editData, location: e.target.value })} />
                        </Grid>

                        {/* Editable Dates */}
                        <Grid className="col-sm-6 mb-3">
                            <TextField
                                label="Gross Date"
                                type="datetime-local"
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={editData.grossDateInput || ""}
                                onChange={(e) => setEditData({ ...editData, grossDateInput: e.target.value })}
                            />
                        </Grid>
                        <Grid className="col-sm-6 mb-3">
                            <TextField
                                label="Tare Date"
                                type="datetime-local"
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={editData.tareDateInput || ""}
                                onChange={(e) => setEditData({ ...editData, tareDateInput: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-12 mb-3">
                            <Divider sx={{ my: 1 }} />
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle1" fontWeight="bold">Products & Billing</Typography>
                                <Button 
                                    size="small" 
                                    variant="outlined" 
                                    startIcon={<AddCircleIcon />} 
                                    onClick={handleAddProduct}
                                >
                                    Add Product
                                </Button>
                            </Box>
                        </Grid>

                        {/* Editable Products mapped from state */}
                        {editData.products?.map((prod, idx) => (
                            <React.Fragment key={idx}>
                                <Grid className="col-sm-3 mb-3">
                                    {/* ✅ SELECT BOX FOR PRODUCT NAME */}
                                    <TextField
                                        select
                                        label="Product Name"
                                        fullWidth
                                        size="small"
                                        value={prod.productName || ""}
                                        onChange={(e) => handleEditProductChange(idx, "productName", e.target.value)}
                                    >
                                        {/* Ensure current value displays even if it was deleted from db productList */}
                                        {prod.productName && !productList.includes(prod.productName) && (
                                            <MenuItem value={prod.productName}>{prod.productName}</MenuItem>
                                        )}
                                        {productList.map((opt, i) => (
                                            <MenuItem key={i} value={opt}>{opt}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid className="col-sm-2 mb-3">
                                    <TextField
                                        label="Qty"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        value={prod.qty || 0}
                                        onChange={(e) => handleEditProductChange(idx, "qty", e.target.value)}
                                    />
                                </Grid>
                                <Grid className="col-sm-3 mb-3">
                                    <TextField
                                        label="Rate"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        value={prod.rate || 0}
                                        onChange={(e) => handleEditProductChange(idx, "rate", e.target.value)}
                                    />
                                </Grid>
                                <Grid className="col-sm-3 mb-3">
                                    <TextField
                                        label="Amount"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        disabled
                                        value={prod.amount || 0}
                                    />
                                </Grid>
                                <Grid className="col-sm-1 mb-3" display="flex" justifyContent="center" alignItems="center">
                                    {/* ✅ DELETE PRODUCT BUTTON */}
                                    <Tooltip title="Delete Product">
                                        <IconButton color="error" onClick={() => handleDeleteProduct(idx)} size="small" sx={{ mt: 0.5 }}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            </React.Fragment>
                        ))}

                        {/* Payment Totals Display */}
                        <Grid className="col-sm-12 mb-2">
                            <Box display="flex" justifyContent="space-between" bgcolor="#f5f5f5" p={2} borderRadius={1} mt={1}>
                                <Typography>Paid: <b>₹{editData.paidAmount || 0}</b></Typography>
                                <Typography color={(editData.remainingAmount || 0) > 0 ? "error" : "success"}>
                                    Remaining: <b>₹{editData.remainingAmount || 0}</b>
                                </Typography>
                                <Typography fontSize={18}>Total Amount: <b>₹{editData.totalAmount || 0}</b></Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={handleUpdate}>Save Quick Edit</Button>
                </DialogActions>
            </Dialog>

            {/* IMAGES DIALOG */}
            <Dialog open={imageOpen} onClose={() => setImageOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Trip Photos</DialogTitle>
                <DialogContent>
                    <Typography fontWeight="bold" mt={1}>Tare Photos</Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                        {selectedImages.tarePhotos.length > 0 ? (
                            selectedImages.tarePhotos.map((url, i) => (
                                <img key={i} src={url} alt={`Tare ${i}`} style={{ width: 250, height: 150, objectFit: "cover", borderRadius: 4, border: "1px solid #ccc" }} />
                            ))
                        ) : (
                            <Typography variant="body2">No Tare photos available</Typography>
                        )}
                    </Box>

                    <Typography fontWeight="bold" mt={1}>Gross Photos</Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {selectedImages.grossPhotos.length > 0 ? (
                            selectedImages.grossPhotos.map((url, i) => (
                                <img key={i} src={url} alt={`Gross ${i}`} style={{ width: 250, height: 150, objectFit: "cover", borderRadius: 4, border: "1px solid #ccc" }} />
                            ))
                        ) : (
                            <Typography variant="body2">No Gross photos available</Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setImageOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

export default Viewtrips;